import { registerLocaleData } from '@angular/common';
import {
    provideHttpClient,
    withInterceptors,
    withXhr,
} from '@angular/common/http';
import {
    ApplicationConfig,
    inject,
    isDevMode,
    provideAppInitializer,
} from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideHippo } from '@hippo/hippo.provider';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { TranslocoHttpLoader } from 'app/core/transloco/transloco.http-loader';
import {
    LogLevel,
    provideAuth as provideOidcAuth,
} from 'angular-auth-oidc-client';
import { environment } from 'environments/environment';
import { lastValueFrom } from 'rxjs';
import { MaintenanceService } from './core/maintenance/maintenance.service';
import { HippoSplashScreenService } from '@hippo/services/splash-screen';
import { apiInterceptor } from './core/services/api.interceptor';
import { ModuleConfigService } from './core/services/module-config.service';

import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeDeExtra from '@angular/common/locales/extra/de';
import localeEnExtra from '@angular/common/locales/extra/en';
import localeFrExtra from '@angular/common/locales/extra/fr';
import localeFr from '@angular/common/locales/fr';
import { APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { Router } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import {
    provideHttpCache,
    withHttpCacheInterceptor,
    withLocalStorage,
} from '@ngneat/cashew';
import * as Sentry from '@sentry/angular';
import {
    CUSTOM_DATE_FORMATS,
    CustomDatePickerAdapter,
} from './core/services/custom-date-adapter.service';
import { PwaService } from './core/services/pwa.service';
import { resetTimerInterceptor } from './core/services/reset-timer.interceptor';

registerLocaleData(localeFr, 'en', localeFrExtra);
registerLocaleData(localeDe, 'de', localeDeExtra);
registerLocaleData(localeEn, 'orgen', localeEnExtra);

export const appConfig: ApplicationConfig = {
    providers: [
        PwaService,
        provideAnimations(),
        provideHttpClient(
            withXhr(),
            withInterceptors([
                apiInterceptor,
                withHttpCacheInterceptor(),
                resetTimerInterceptor,
            ])
        ),
        provideHttpCache(withLocalStorage()),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
            // withHashLocation()
        ),
        { provide: DateAdapter, useClass: CustomDatePickerAdapter },
        { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },

        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'de',
                        label: 'Deutsch',
                    },
                    {
                        id: 'en',
                        label: 'English',
                    },
                ],
                defaultLang: 'de',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: true,
                missingHandler: {
                    useFallbackTranslation: true,
                    logMissingKey: true,
                },
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(() => {
            const moduleConfigService = inject(ModuleConfigService);
            return moduleConfigService.load();
        }),
        provideAppInitializer(() => {
            const initializerFn = (() => {
                const translocoService = inject(TranslocoService);
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);
                return () => lastValueFrom(translocoService.load(defaultLang));
            })();
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const maintenanceService = inject(MaintenanceService);
            const splashScreenService = inject(HippoSplashScreenService);
            const translocoService = inject(TranslocoService);
            const defaultLang = translocoService.getDefaultLang();
            return Promise.all([
                maintenanceService.checkMaintenance(),
                lastValueFrom(translocoService.load(`codext/${defaultLang}`)),
            ]).then(() => {
                if (maintenanceService.isActive()) {
                    splashScreenService.hide();
                }
            });
        }),
        provideAuth(),
        provideOidcAuth({
            config: {
                authority: environment.cognito.authority,
                redirectUrl: environment.cognito.redirectUrl,
                postLogoutRedirectUri:
                    environment.cognito.postLogoutRedirectUri,
                clientId: environment.cognito.clientId,
                scope: environment.cognito.scope,
                responseType: environment.cognito.responseType,
                silentRenew: true,
                useRefreshToken: true,
                renewTimeBeforeTokenExpiresInSeconds: 30,
                logLevel: environment.production
                    ? LogLevel.None
                    : LogLevel.Warn,
            },
        }),
        provideIcons(),
        provideHippo({
            hippo: {
                layout: 'classic',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-brand',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                ],
            },
        }),
        provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000',
        }),
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
        },
    ],
};
